import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Send, Loader2, MessageSquare, Sparkles, Image as ImageIcon, Paperclip } from "lucide-react";
import { cn } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  imageUrl?: string;
  referenceImages?: string[];
  isGenerating?: boolean;
}

interface CreativeChatProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  imageUrl: string;
  creativeId: number | null;
  onImageGenerated: (url: string) => void;
  imageModel?: string;
}

export function CreativeChat({ open, onOpenChange, imageUrl, creativeId, onImageGenerated, imageModel }: CreativeChatProps) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [referenceImages, setReferenceImages] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await fetch("/api/upload", { method: "POST", body: formData });
        const data = await res.json();
        if (data.url) {
          setReferenceImages((prev) => [...prev, data.url]);
        }
      }
    } catch {
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeReferenceImage = (index: number) => {
    setReferenceImages((prev) => prev.filter((_, i) => i !== index));
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const currentRefs = [...referenceImages];
    const userMsg: ChatMessage = { role: "user", content: text, referenceImages: currentRefs.length > 0 ? currentRefs : undefined };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setReferenceImages([]);
    setLoading(true);

    try {
      const apiMessages = newMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const res = await apiRequest("POST", "/api/creatives/chat", {
        messages: apiMessages,
        imageUrl,
        creativeId: creativeId || undefined,
        referenceImageUrls: currentRefs.length > 0 ? currentRefs : undefined,
        imageModel: imageModel || undefined,
      });
      const data = await res.json();

      if (data.type === "generation") {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.message, isGenerating: true },
        ]);

        setTimeout(() => {
          setMessages((prev) =>
            prev.map((m, i) =>
              i === prev.length - 1
                ? { ...m, isGenerating: false, imageUrl: data.imageUrl }
                : m
            )
          );
          onImageGenerated(data.imageUrl);
        }, 500);
      } else {
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.message },
        ]);
      }
    } catch (err: any) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Sorry, something went wrong. Please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className={cn(
        "fixed top-0 right-0 h-full bg-white border-l border-gray-200 shadow-2xl z-50 flex flex-col transition-transform duration-300 ease-in-out",
        open ? "translate-x-0" : "translate-x-full"
      )}
      style={{ width: 380 }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/80 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-indigo-100 flex items-center justify-center">
            <Sparkles className="h-3.5 w-3.5 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-900">AI Creative Director</h3>
            <p className="text-[10px] text-gray-500">Describe changes to your ad</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-gray-400 hover:text-gray-600"
          onClick={() => onOpenChange(false)}
          data-testid="button-close-chat"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center px-6">
            <div className="h-14 w-14 rounded-2xl bg-indigo-50 flex items-center justify-center mb-4">
              <MessageSquare className="h-7 w-7 text-indigo-400" />
            </div>
            <h4 className="text-sm font-semibold text-gray-900 mb-1">Chat with AI</h4>
            <p className="text-xs text-gray-500 leading-relaxed">
              Describe what you'd like to change about this creative. For example: "Make the headline yellow" or "Add a person holding the product in the background"
            </p>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={cn("flex", msg.role === "user" ? "justify-end" : "justify-start")}>
            <div
              className={cn(
                "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed",
                msg.role === "user"
                  ? "bg-indigo-600 text-white rounded-br-md"
                  : "bg-gray-100 text-gray-800 rounded-bl-md"
              )}
            >
              {msg.referenceImages && msg.referenceImages.length > 0 && (
                <div className="flex gap-1.5 mb-2">
                  {msg.referenceImages.map((refUrl, ri) => (
                    <img
                      key={ri}
                      src={refUrl}
                      alt={`Reference ${ri + 1}`}
                      className="h-14 w-14 rounded-lg object-cover border border-white/20"
                    />
                  ))}
                </div>
              )}
              <p className="whitespace-pre-wrap">{msg.content}</p>
              {msg.isGenerating && (
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-200/50">
                  <Loader2 className="h-3 w-3 animate-spin text-indigo-500" />
                  <span className="text-[11px] text-indigo-600 font-medium">Generating edit...</span>
                </div>
              )}
              {msg.imageUrl && (
                <div className="mt-2 pt-2 border-t border-gray-200/50">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <ImageIcon className="h-3 w-3 text-green-600" />
                    <span className="text-[11px] text-green-700 font-medium">Edit applied to canvas</span>
                  </div>
                  <img
                    src={msg.imageUrl}
                    alt="Generated edit"
                    className="w-full rounded-lg border border-gray-200"
                  />
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <div className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                  <div className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                  <div className="h-1.5 w-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-gray-100 px-4 py-3 bg-white flex-shrink-0">
        {referenceImages.length > 0 && (
          <div className="flex gap-2 mb-2 flex-wrap">
            {referenceImages.map((refUrl, i) => (
              <div key={i} className="relative group">
                <img
                  src={refUrl}
                  alt={`Ref ${i + 1}`}
                  className="h-12 w-12 rounded-lg object-cover border border-gray-200"
                />
                <button
                  onClick={() => removeReferenceImage(i)}
                  className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`button-remove-ref-${i}`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileUpload}
          />
          <Button
            data-testid="button-chat-upload-ref"
            variant="ghost"
            size="icon"
            className={cn(
              "h-10 w-10 rounded-xl flex-shrink-0 border",
              referenceImages.length > 0
                ? "border-indigo-200 bg-indigo-50 text-indigo-600"
                : "border-gray-200 bg-gray-50 text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            )}
            onClick={() => fileInputRef.current?.click()}
            disabled={loading || uploading}
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Paperclip className="h-4 w-4" />
            )}
          </Button>
          <Input
            ref={inputRef}
            data-testid="input-chat-message"
            placeholder="Describe what to change..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
            className="flex-1 h-10 rounded-xl border-gray-200 text-sm bg-gray-50/50"
            disabled={loading}
          />
          <Button
            data-testid="button-send-chat"
            size="icon"
            className={cn(
              "h-10 w-10 rounded-xl flex-shrink-0",
              input.trim() && !loading
                ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            )}
            onClick={sendMessage}
            disabled={!input.trim() || loading}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
